import { useCallback, useEffect, useMemo, useRef, useState, type WheelEvent } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { Download, X } from "lucide-react";

import styles from "./EndReportFlow.module.css";
import { getToken, useMsalAuth } from "../../authConfig";
import { generateEndReportApi } from "../../api/api";
import { Project } from "../../api/models";
import { useProjects } from "../../contexts/ProjectContext";
import { AnswerLoading } from "../Answer";
import { UserChatMessage } from "../UserChatMessage";

interface EndReportFlowProps {
    onDismiss: () => void;
    projectId?: string;
}

type Stage = "selectProject" | "generating" | "ready" | "error";
type FailedAction = "loadProjects" | "generate" | null;

export const EndReportFlow = ({ onDismiss, projectId }: EndReportFlowProps) => {
    const { t, i18n } = useTranslation();
    const client = useMsalAuth ? useMsal().instance : undefined;
    const { projects, loadProjects } = useProjects();

    const [stage, setStage] = useState<Stage>(projectId ? "generating" : "selectProject");
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(projectId ?? null);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [failedAction, setFailedAction] = useState<FailedAction>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [filename, setFilename] = useState<string>("slutrapport.docx");
    const [attempt, setAttempt] = useState<number>(0);

    const bootstrapRef = useRef(false);
    const projectChipsRowRef = useRef<HTMLDivElement | null>(null);

    const availableProjects = useMemo(
        () => [...projects].sort((left, right) => left.name.localeCompare(right.name, i18n.language)),
        [projects, i18n.language]
    );

    const runLoadProjects = useCallback(async () => {
        setFailedAction(null);
        setErrorMessage("");
        setStage("selectProject");
        try {
            await loadProjects();
        } catch (loadError) {
            console.error("Failed to load projects:", loadError);
            const message = loadError instanceof Error ? loadError.message : t("endReportFlow.error");
            setErrorMessage(message);
            setFailedAction("loadProjects");
            setStage("error");
        }
    }, [loadProjects, t]);

    useEffect(() => {
        if (bootstrapRef.current) return;
        bootstrapRef.current = true;

        if (projectId) {
            return;
        }

        if (projects.length === 0) {
            void runLoadProjects();
        }
    }, [projectId, projects.length, runLoadProjects]);

    useEffect(() => {
        if (!selectedProjectId) {
            return;
        }

        const controller = new AbortController();
        let createdBlobUrl: string | null = null;
        let cancelled = false;

        const run = async () => {
            setErrorMessage("");
            setFailedAction(null);

            let token: string | undefined = "";
            try {
                if (client) {
                    token = await getToken(client);
                }
            } catch (authError) {
                console.error("Failed to resolve auth token:", authError);
            }

            try {
                const response = await generateEndReportApi(selectedProjectId, token, controller.signal);
                const blob = await response.blob();
                if (cancelled) return;

                const disposition = response.headers.get("Content-Disposition") || "";
                const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
                const parsedFilename = match ? decodeURIComponent(match[1]) : "slutrapport.docx";
                createdBlobUrl = URL.createObjectURL(blob);

                setFilename(parsedFilename);
                setBlobUrl(createdBlobUrl);
                setStage("ready");
            } catch (error) {
                if (controller.signal.aborted || cancelled) return;
                const message = error instanceof Error ? error.message : t("endReportFlow.error");
                setErrorMessage(message);
                setFailedAction("generate");
                setStage("error");
            }
        };

        run();

        return () => {
            cancelled = true;
            controller.abort();
            if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
        };
    }, [selectedProjectId, attempt, client, t]);

    const handleProjectSelected = useCallback((project: Project) => {
        setSelectedProjectId(project.id);
        setStage("generating");
    }, []);

    const handleProjectChipsWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
        const container = projectChipsRowRef.current;
        if (!container) {
            return;
        }

        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
            return;
        }

        const canScrollHorizontally = container.scrollWidth > container.clientWidth;
        if (!canScrollHorizontally) {
            return;
        }

        event.preventDefault();
        container.scrollLeft += event.deltaY;
    }, []);

    const handleRetry = useCallback(() => {
        if (failedAction === "loadProjects") {
            void runLoadProjects();
            return;
        }
        setAttempt(prev => prev + 1);
        setStage("generating");
    }, [failedAction, runLoadProjects]);

    const handleDownload = useCallback(() => {
        if (!blobUrl) return;
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [blobUrl, filename]);

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <button type="button" className={styles.dismissButton} onClick={onDismiss}>
                    <X size={18} />
                    {t("endReportFlow.close")}
                </button>

                <UserChatMessage message={t("endReportFlow.userLabel")} />

                <div className={styles.aiMessage}>{t("endReportFlow.intro")}</div>

                {stage === "selectProject" && (
                    <>
                        <div className={styles.aiMessage}>{t("endReportFlow.selectProjectLabel")}</div>
                        {availableProjects.length > 0 ? (
                            <div
                                ref={projectChipsRowRef}
                                className={styles.projectChipsRow}
                                onWheel={handleProjectChipsWheel}
                            >
                                {availableProjects.map(project => (
                                    <button
                                        key={project.id}
                                        type="button"
                                        className={styles.projectChip}
                                        onClick={() => handleProjectSelected(project)}
                                    >
                                        {project.name}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.aiMessage}>{t("endReportFlow.noProjects")}</div>
                        )}
                    </>
                )}

                {stage === "generating" && (
                    <div className={styles.noteContentWrapper}>
                        <AnswerLoading />
                    </div>
                )}

                {stage === "ready" && (
                    <div className={styles.aiMessage}>
                        {t("endReportFlow.readyMessage")}{" "}
                        <button type="button" className={styles.downloadLink} onClick={handleDownload}>
                            <Download size={14} />
                            {t("endReportFlow.downloadLink")}
                        </button>
                    </div>
                )}

                {stage === "error" && (
                    <div className={styles.aiMessage}>
                        {errorMessage || t("endReportFlow.error")}{" "}
                        <button type="button" className={styles.retryLink} onClick={handleRetry}>
                            {t("endReportFlow.retry")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
